import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, gql } from "@apollo/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SpreadsheetIcon, EditIcon, SpinnerIcon, LockIcon } from "@/lib/icons";
import {
  PreviewPackage,
  PreviewTask,
  PreviewWorkSection,
  PreviewInspectionTask,
  PreviewDocumentTask,
  PreviewSubtask,
} from "./TaskTemplatePreviewTable";
import {
  TaskTemplateColumnView,
  type TemplateEditingApi,
} from "./TaskTemplateColumnView";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { offsetFromColumns, offsetToColumns } from "./deadlineOffset";
import {
  useTemplateSetDraft,
  type DraftNewTask,
} from "./useTemplateSetDraft";

const GET_TEMPLATE_TASKS = gql`
  query GetTemplateTasks($taskTemplateSetId: String!) {
    templateTasks(filter: { taskTemplateSetId: $taskTemplateSetId }, limit: 10000) {
      items {
        id
        name
        description
        version
        templateWorkSectionId
        templateProcurementPackageId
        parentTemplateTaskId
        congridInspectionTemplateId
        startTimingOffsetDays
        endTimingOffsetDays
        area
      }
      totalCount
    }
  }
`;

const GET_TEMPLATE_STRUCTURE = gql`
  query GetTemplateStructure($taskTemplateSetId: String!) {
    templateProcurementPackages(
      filter: { taskTemplateSetId: $taskTemplateSetId }
      limit: 1000
    ) {
      items {
        id
        code
        name
      }
    }
    templateWorkSections(
      filter: { taskTemplateSetId: $taskTemplateSetId }
      limit: 10000
    ) {
      items {
        id
        code
        name
        templateProcurementPackageId
      }
    }
  }
`;

interface TemplateTask {
  id: string;
  name: string;
  description: string | null;
  version: number;
  templateWorkSectionId: string | null;
  templateProcurementPackageId: string | null;
  parentTemplateTaskId: string | null;
  congridInspectionTemplateId: string | null;
  startTimingOffsetDays: number | null;
  endTimingOffsetDays: number | null;
  isNew?: boolean;
  isDeleted?: boolean;
}

interface StructurePackage {
  id: string;
  code: string;
  name: string | null;
}

interface StructureWorkSection {
  id: string;
  code: string;
  name: string | null;
  templateProcurementPackageId: string | null;
}

interface TaskTemplateViewerProps {
  taskTemplateSetId: string;
  maxHeight?: string;
  /** Whether this pohja may be edited at all (no hanke uses it). */
  editable?: boolean;
  /** Names of the hankkeet locking this pohja, shown when not editable. */
  lockedByProjects?: string[];
  /** Saves a draft; returns the mutation result so errors can be shown. */
  onSave?: (payload: {
    creates: unknown[];
    updates: unknown[];
    deleteIds: string[];
  }) => Promise<
    { success: boolean; message: string; errors: string[] } | undefined
  >;
  saving?: boolean;
  /** Reports whether unsaved edits exist, so the dialog can guard closing. */
  onDirtyChange?: (dirty: boolean) => void;
}

const UNASSIGNED_PACKAGE_ID = "unassigned-package";

function deadlineText(
  start: number | null | undefined,
  end: number | null | undefined
): string {
  if (start != null) {
    return start < 0
      ? `${-start} pv alun jälkeen`
      : start === 0
      ? "alkupäivänä"
      : `${start} pv ennen alkua`;
  }
  if (end != null) {
    return end < 0
      ? `${-end} pv lopun jälkeen`
      : end === 0
      ? "loppupäivänä"
      : `${end} pv ennen loppua`;
  }
  return "—";
}

const CONGRID_ORDER = [
  "malliasennustarkastus",
  "osakohteen tarkastus",
  "osakohdetarkastus",
  "mestan vastaanotto",
];
const congridRank = (name: string): number => {
  const n = name.trim().toLowerCase();
  const idx = CONGRID_ORDER.findIndex((k) => n.startsWith(k));
  return idx === -1 ? CONGRID_ORDER.length : idx === 2 ? 1 : idx;
};

const leadingNumber = (s: string): number => {
  const m = s.trim().match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
};

/**
 * Group tasks into the package → work section → task tree the column view renders.
 *
 * The skeleton comes from the structure query rather than from the tasks, so a
 * hankintapaketti or työvaihe that holds no tehtävä yet is still reachable in
 * edit mode. Empty containers stay hidden while reading.
 */
function transformToPreview(
  tasks: TemplateTask[],
  packages: StructurePackage[],
  workSections: StructureWorkSection[],
  editing: boolean
): PreviewPackage[] {
  const parents: TemplateTask[] = [];
  const subtasksByParent = new Map<string, TemplateTask[]>();

  tasks.forEach((t) => {
    if (!t.parentTemplateTaskId) parents.push(t);
    else {
      const list = subtasksByParent.get(t.parentTemplateTaskId) ?? [];
      list.push(t);
      subtasksByParent.set(t.parentTemplateTaskId, list);
    }
  });

  subtasksByParent.forEach((list) =>
    list.sort((a, b) => {
      const na = leadingNumber(a.name);
      const nb = leadingNumber(b.name);
      return na !== nb ? na - nb : a.name.localeCompare(b.name);
    })
  );

  const toSubtasks = (parentId: string, parentIsNew: boolean): PreviewSubtask[] =>
    (subtasksByParent.get(parentId) ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description,
      isNew: s.isNew,
      isDeleted: s.isDeleted,
      parentId,
      parentIsNew,
    }));

  const packageById = new Map<string, PreviewPackage>();
  const wsById = new Map<string, PreviewWorkSection>();
  const wsPackage = new Map<string, string>();

  packages.forEach((p) => {
    packageById.set(p.id, {
      id: p.id,
      code: p.code,
      name: p.name,
      tasks: [],
      workSections: [],
    });
  });

  workSections.forEach((ws) => {
    const pkgId = ws.templateProcurementPackageId ?? UNASSIGNED_PACKAGE_ID;
    if (!packageById.has(pkgId)) {
      packageById.set(pkgId, {
        id: pkgId,
        code: "—",
        name: null,
        tasks: [],
        workSections: [],
      });
    }
    const preview: PreviewWorkSection = {
      id: ws.id,
      code: ws.code,
      name: ws.name,
      inspectionTasks: [],
      documentTasks: [],
    };
    wsById.set(ws.id, preview);
    wsPackage.set(ws.id, pkgId);
    packageById.get(pkgId)!.workSections.push(preview);
  });

  const ensurePackage = (id: string | null): PreviewPackage => {
    const key = id ?? UNASSIGNED_PACKAGE_ID;
    if (!packageById.has(key)) {
      packageById.set(key, {
        id: key,
        code: "—",
        name: null,
        tasks: [],
        workSections: [],
      });
    }
    return packageById.get(key)!;
  };

  parents.forEach((task) => {
    const pkgId = task.templateProcurementPackageId;
    const wsId = task.templateWorkSectionId;

    if (!wsId) {
      ensurePackage(pkgId).tasks.push({
        id: task.id,
        name: task.name,
        description: task.description,
        deadlineText: deadlineText(
          task.startTimingOffsetDays,
          task.endTimingOffsetDays
        ),
        subtasks: toSubtasks(task.id, !!task.isNew),
        isNew: task.isNew,
        isDeleted: task.isDeleted,
        startTimingOffsetDays: task.startTimingOffsetDays,
        endTimingOffsetDays: task.endTimingOffsetDays,
        packageId: pkgId,
        workSectionId: null,
      } as PreviewTask);
      return;
    }

    const ws = wsById.get(wsId);
    if (!ws) return;

    if (task.congridInspectionTemplateId !== null) {
      ws.inspectionTasks.push({
        id: task.id,
        name: task.name,
        description: task.description ?? "",
        deadlineText: deadlineText(
          task.startTimingOffsetDays,
          task.endTimingOffsetDays
        ),
        congridId: task.congridInspectionTemplateId ?? "",
        isDeleted: task.isDeleted,
        errors:
          editing && !(task.congridInspectionTemplateId ?? "").trim()
            ? ["Congrid ID puuttuu"]
            : undefined,
        isNew: task.isNew,
        startTimingOffsetDays: task.startTimingOffsetDays,
        endTimingOffsetDays: task.endTimingOffsetDays,
        packageId: wsPackage.get(wsId) ?? pkgId,
        workSectionId: wsId,
      } as PreviewInspectionTask);
    } else {
      ws.documentTasks.push({
        id: task.id,
        name: task.name,
        deadlineText: deadlineText(
          task.startTimingOffsetDays,
          task.endTimingOffsetDays
        ),
        subtasks: toSubtasks(task.id, !!task.isNew),
        isNew: task.isNew,
        isDeleted: task.isDeleted,
        startTimingOffsetDays: task.startTimingOffsetDays,
        endTimingOffsetDays: task.endTimingOffsetDays,
        packageId: wsPackage.get(wsId) ?? pkgId,
        workSectionId: wsId,
      } as PreviewDocumentTask);
    }
  });

  const result = Array.from(packageById.values());
  result.forEach((pkg) => {
    pkg.workSections.sort((a, b) => a.code.localeCompare(b.code));
    pkg.workSections.forEach((ws) =>
      ws.inspectionTasks.sort((a, b) => congridRank(a.name) - congridRank(b.name))
    );
    if (!editing) {
      pkg.workSections = pkg.workSections.filter(
        (ws) => ws.inspectionTasks.length > 0 || ws.documentTasks.length > 0
      );
    }
  });

  const visible = editing
    ? result
    : result.filter(
        (pkg) => pkg.tasks.length > 0 || pkg.workSections.length > 0
      );

  return visible.sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Problems the editor can see without asking the server.
 *
 * Mirrors two of the backend's rules so an obviously incomplete draft is
 * refused instantly instead of after a round trip that reloads every task in
 * the pohja. The backend remains the authority: anything missed here is
 * caught there.
 */
function localDraftErrors(newTasks: DraftNewTask[]): string[] {
  const errors: string[] = [];
  newTasks.forEach((t) => {
    if (!t.name.trim()) {
      errors.push("Tehtävän nimike ei voi olla tyhjä.");
      return;
    }
    if (t.kind === "INSPECTION" && !(t.congridInspectionTemplateId ?? "").trim()) {
      errors.push(`Tarkastustehtävä '${t.name}' vaatii Congrid ID:n.`);
    }
  });
  return errors;
}

/** Apply the draft to the server rows so the tree shows unsaved edits. */
function mergeDraft(
  serverTasks: TemplateTask[],
  patches: Record<string, { description?: string | null; congridInspectionTemplateId?: string | null; name?: string; offset?: ReturnType<typeof offsetFromColumns> }>,
  newTasks: DraftNewTask[],
  deletedIds: string[]
): TemplateTask[] {
  const deleted = new Set(deletedIds);
  const merged: TemplateTask[] = [];

  serverTasks.forEach((task) => {
    // A row queued for deletion stays in the tree so it can be shown struck
    // through until Tallenna actually removes it; so do its alitehtävät,
    // which the save cascades over.
    const isDeleted =
      deleted.has(task.id) ||
      (!!task.parentTemplateTaskId && deleted.has(task.parentTemplateTaskId));

    const patch = patches[task.id];
    if (!patch) {
      merged.push(isDeleted ? { ...task, isDeleted: true } : task);
      return;
    }
    const cols = patch.offset
      ? offsetToColumns(patch.offset)
      : {
          startTimingOffsetDays: task.startTimingOffsetDays,
          endTimingOffsetDays: task.endTimingOffsetDays,
        };
    merged.push({
      ...task,
      isDeleted: isDeleted || undefined,
      name: patch.name ?? task.name,
      description:
        patch.description !== undefined ? patch.description : task.description,
      congridInspectionTemplateId:
        patch.congridInspectionTemplateId !== undefined
          ? patch.congridInspectionTemplateId
          : task.congridInspectionTemplateId,
      startTimingOffsetDays: cols.startTimingOffsetDays,
      endTimingOffsetDays: cols.endTimingOffsetDays,
    });
  });

  newTasks.forEach((t) => {
    const cols = offsetToColumns(t.offset);
    merged.push({
      id: t.tempId,
      name: t.name,
      description: t.description,
      version: 0,
      templateWorkSectionId: t.templateWorkSectionId,
      templateProcurementPackageId: t.templateProcurementPackageId,
      parentTemplateTaskId: t.parentTempId ?? t.parentTemplateTaskId,
      congridInspectionTemplateId: t.congridInspectionTemplateId,
      startTimingOffsetDays: cols.startTimingOffsetDays,
      endTimingOffsetDays: cols.endTimingOffsetDays,
      isNew: true,
    });
  });

  return merged;
}

export function TaskTemplateViewer({
  taskTemplateSetId,
  maxHeight,
  editable = false,
  lockedByProjects = [],
  onSave,
  saving = false,
  onDirtyChange,
}: TaskTemplateViewerProps) {
  const { data, loading, error, refetch } = useQuery(GET_TEMPLATE_TASKS, {
    variables: { taskTemplateSetId },
    skip: !taskTemplateSetId,
  });
  const { data: structureData } = useQuery(GET_TEMPLATE_STRUCTURE, {
    variables: { taskTemplateSetId },
    skip: !taskTemplateSetId,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [revealTaskId, setRevealTaskId] = useState<string | null>(null);
  const [confirmStopEditing, setConfirmStopEditing] = useState(false);

  // Hankintapaketit and työvaiheet touched since the dialog opened. Kept
  // across Tallenna and across leaving edit mode, and reset only by the
  // dialog unmounting, which is what ends the session.
  const [touchedPackageIds, setTouchedPackageIds] = useState<Set<string>>(
    () => new Set()
  );
  const [touchedWorkSectionIds, setTouchedWorkSectionIds] = useState<Set<string>>(
    () => new Set()
  );
  const [touchedTaskIds, setTouchedTaskIds] = useState<Set<string>>(
    () => new Set()
  );

  const markTaskTouched = useCallback((id: string) => {
    setTouchedTaskIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const draft = useTemplateSetDraft();

  const serverTasks: TemplateTask[] = useMemo(
    () => data?.templateTasks?.items ?? [],
    [data]
  );
  const structurePackages: StructurePackage[] = useMemo(
    () => structureData?.templateProcurementPackages?.items ?? [],
    [structureData]
  );
  const structureWorkSections: StructureWorkSection[] = useMemo(
    () => structureData?.templateWorkSections?.items ?? [],
    [structureData]
  );

  const packages = useMemo(() => {
    const merged = draft.isDirty
      ? mergeDraft(
          serverTasks,
          draft.patches,
          draft.newTasks,
          draft.deletedIds
        )
      : serverTasks;
    return transformToPreview(
      merged,
      structurePackages,
      structureWorkSections,
      isEditing
    );
  }, [
    serverTasks,
    structurePackages,
    structureWorkSections,
    isEditing,
    draft.isDirty,
    draft.patches,
    draft.newTasks,
    draft.deletedIds,
  ]);

  /** Every task in the set by id, for resolving a row to its containers. */
  const serverTaskById = useMemo(() => {
    const map = new Map<string, TemplateTask>();
    serverTasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [serverTasks]);

  const markTouched = useCallback(
    (packageId: string | null, workSectionId: string | null) => {
      if (packageId) {
        setTouchedPackageIds((prev) =>
          prev.has(packageId) ? prev : new Set(prev).add(packageId)
        );
      }
      if (workSectionId) {
        setTouchedWorkSectionIds((prev) =>
          prev.has(workSectionId) ? prev : new Set(prev).add(workSectionId)
        );
      }
    },
    []
  );

  /**
   * Mark the containers a row belongs to.
   *
   * An alitehtävä may carry no package or työvaihe of its own, so an
   * unresolved row falls back to its päätehtävä.
   */
  const markTouchedByTaskId = useCallback(
    (id: string) => {
      const draftRow = draft.newTasks.find((t) => t.tempId === id);
      if (draftRow) {
        if (draftRow.templateProcurementPackageId || draftRow.templateWorkSectionId) {
          markTouched(
            draftRow.templateProcurementPackageId,
            draftRow.templateWorkSectionId
          );
          return;
        }
        const parentId = draftRow.parentTempId ?? draftRow.parentTemplateTaskId;
        if (parentId) markTouchedByTaskId(parentId);
        return;
      }

      const row = serverTaskById.get(id);
      if (!row) return;
      if (row.templateProcurementPackageId || row.templateWorkSectionId) {
        markTouched(row.templateProcurementPackageId, row.templateWorkSectionId);
        return;
      }
      if (row.parentTemplateTaskId) markTouchedByTaskId(row.parentTemplateTaskId);
    },
    [draft.newTasks, serverTaskById, markTouched]
  );

  const editingApi: TemplateEditingApi | undefined = isEditing
    ? {
        onPatchTask: (id, patch, isNew) => {
          if (isNew) draft.patchNewTask(id, patch);
          else draft.patchTask(id, patch);
          markTouchedByTaskId(id);
          markTaskTouched(id);
        },
        onAddTask: ({ kind, name, packageId, workSectionId }) => {
          const tempId = draft.addTask({
            name,
            kind,
            templateProcurementPackageId: packageId,
            templateWorkSectionId: kind === "PROCUREMENT" ? null : workSectionId,
            parentTempId: null,
            parentTemplateTaskId: null,
            description: null,
            congridInspectionTemplateId: kind === "INSPECTION" ? "" : null,
            offset: { mode: "NONE" },
          });
          setRevealTaskId(tempId);
          markTouched(packageId, kind === "PROCUREMENT" ? null : workSectionId);
          markTaskTouched(tempId);
        },
        onAddSubtask: ({ parentId, isNewParent, packageId, workSectionId }) => {
          const tempId = draft.addTask({
            name: "Uusi alitehtävä",
            kind: "SUBTASK",
            templateProcurementPackageId: packageId,
            templateWorkSectionId: workSectionId,
            parentTempId: isNewParent ? parentId : null,
            parentTemplateTaskId: isNewParent ? null : parentId,
            description: null,
            congridInspectionTemplateId: null,
            offset: { mode: "NONE" },
          });
          setRevealTaskId(tempId);
          markTouched(packageId, workSectionId);
          markTaskTouched(tempId);
          markTaskTouched(parentId);
        },
        onDeleteTask: ({ id, isNew }) => {
          markTouchedByTaskId(id);
          if (isNew) draft.removeNewTask(id);
          else draft.deleteExisting(id);
        },
        onUndeleteTask: (id) => {
          // A struck alitehtävä may owe its state to a deleted päätehtävä, so
          // undo whichever row is actually queued for deletion.
          let target: string | null = id;
          while (target && !draft.deletedIds.includes(target)) {
            target = serverTaskById.get(target)?.parentTemplateTaskId ?? null;
          }
          if (target) draft.undeleteExisting(target);
        },
      }
    : undefined;

  const clearReveal = useCallback(() => setRevealTaskId(null), []);

  const startEditing = () => {
    setSaveErrors([]);
    setIsEditing(true);
  };

  const stopEditing = () => {
    if (draft.isDirty) {
      setConfirmStopEditing(true);
      return;
    }
    setSaveErrors([]);
    setIsEditing(false);
  };

  const cancelEditing = () => {
    draft.reset();
    setSaveErrors([]);
    setIsEditing(false);
    onDirtyChange?.(false);
  };

  const handleSave = async () => {
    if (!onSave) return;
    const tStart = performance.now();
    const versions: Record<string, number> = {};
    serverTasks.forEach((t) => {
      versions[t.id] = t.version;
    });
    const localErrors = localDraftErrors(draft.newTasks);
    if (localErrors.length > 0) {
      setSaveErrors(localErrors);
      return;
    }

    const payload = draft.toSavePayload(versions);
    const tBuilt = performance.now();
    const result = await onSave(payload);
    const tAnswered = performance.now();
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        console.log(
          "[tehtavapohja-save]",
          JSON.stringify({
            creates: payload.creates.length,
            updates: payload.updates.length,
            deletes: payload.deleteIds.length,
            serverTasks: serverTasks.length,
            buildMs: Math.round(tBuilt - tStart),
            requestMs: Math.round(tAnswered - tBuilt),
            repaintMs: Math.round(performance.now() - tAnswered),
            success: result?.success,
          })
        )
      )
    );
    if (result?.success) {
      draft.reset();
      setSaveErrors([]);
      onDirtyChange?.(false);
      await refetch();
    } else {
      setSaveErrors(
        result?.errors?.length
          ? result.errors
          : [result?.message ?? "Tallennus epäonnistui"]
      );
    }
  };

  // Keep the dialog's close guard in step with the draft. This has to be an
  // effect: reporting upward during render updates the parent mid-render.
  const dirty = draft.isDirty;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const flexMode = maxHeight !== undefined;

  const statusCard = (content: ReactNode) => (
    <Card className={flexMode ? "h-full flex flex-col" : ""}>
      <CardContent
        className={`py-8${flexMode ? " flex-1 min-h-0 flex items-center justify-center" : ""}`}
      >
        {content}
      </CardContent>
    </Card>
  );

  if (loading) {
    return statusCard(
      <div className="flex items-center justify-center text-muted-foreground">
        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mr-2" />
        Ladataan tehtäviä...
      </div>
    );
  }

  if (error) {
    return statusCard(
      <div className="text-destructive text-center">
        Virhe tehtävien latauksessa: {error.message}
      </div>
    );
  }

  if (packages.length === 0) {
    return statusCard(
      <div className="text-center text-muted-foreground">
        Ei tehtäviä tässä tehtäväpohjassa
      </div>
    );
  }

  const totalWorkSections = packages.reduce(
    (s, p) => s + p.workSections.length,
    0
  );
  const totalTasks = packages.reduce(
    (s, p) =>
      s +
      p.tasks.length +
      p.workSections.reduce(
        (ws, w) => ws + w.inspectionTasks.length + w.documentTasks.length,
        0
      ),
    0
  );
  const totalSubtasks = packages.reduce(
    (s, p) =>
      s +
      p.tasks.reduce((ts, t) => ts + t.subtasks.length, 0) +
      p.workSections.reduce(
        (ws, w) => ws + w.documentTasks.reduce((ds, dt) => ds + dt.subtasks.length, 0),
        0
      ),
    0
  );

  return (
    <Card className={`overflow-hidden${flexMode ? " h-full flex flex-col" : ""}`}>
      <ConfirmDialog
        open={confirmStopEditing}
        onOpenChange={setConfirmStopEditing}
        title="Poistu muokkaustilasta"
        description="Sinulla on tallentamattomia muutoksia. Ne säilyvät näkyvissä, kunnes suljet ikkunan, mutta niitä ei ole vielä tallennettu."
        confirmLabel="Poistu tallentamatta"
        onConfirm={() => {
          setConfirmStopEditing(false);
          setSaveErrors([]);
          setIsEditing(false);
        }}
      />
      <CardHeader className="flex flex-row items-center justify-between gap-3 py-3 px-4 border-b bg-muted/20">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <SpreadsheetIcon className="h-4 w-4 text-primary" />
          Sisältö
        </CardTitle>

        <div className="flex items-center gap-2">
          {!editable && (
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <LockIcon className="h-3 w-3" />
              {lockedByProjects.length > 0
                ? `Käytössä hankkeissa: ${lockedByProjects.join(", ")}. Tee kopio muokataksesi.`
                : "Tehtäväpohjaa ei voi muokata."}
            </span>
          )}
          {editable && dirty && (
            <span className="text-[11px] font-medium text-amber-600 dark:text-amber-500">
              Tallentamattomia muutoksia
            </span>
          )}
          {editable && !isEditing && (
            <>
              {dirty && (
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving && (
                    <SpinnerIcon className="mr-2 h-3.5 w-3.5 animate-spin" />
                  )}
                  Tallenna
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={startEditing}>
                <EditIcon className="mr-2 h-3.5 w-3.5" />
                Muokkaa
              </Button>
            </>
          )}
          {editable && isEditing && (
            <>
              <span className="text-[11px] font-medium text-primary">
                Muokkaustila
              </span>
              <Button size="sm" variant="outline" onClick={stopEditing}>
                Lopeta muokkaus
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelEditing}>
                Peruuta muutokset
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
                {saving && <SpinnerIcon className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Tallenna
              </Button>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent
        className={`px-0 pb-0 overflow-x-hidden${flexMode ? " flex-1 min-h-0 flex flex-col" : ""}`}
      >
        {saveErrors.length > 0 && (
          <div className="mx-4 mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
            {saveErrors.map((e, i) => (
              <p key={i} className="text-xs text-destructive leading-tight">
                {e}
              </p>
            ))}
          </div>
        )}

        {flexMode ? (
          <div className="flex-1 min-h-0">
            <TaskTemplateColumnView
              packages={packages}
              maxHeight="100%"
              editing={editingApi}
              revealTaskId={revealTaskId}
              onRevealed={clearReveal}
              touchedPackageIds={touchedPackageIds}
              touchedWorkSectionIds={touchedWorkSectionIds}
              touchedTaskIds={touchedTaskIds}
            />
          </div>
        ) : (
          <TaskTemplateColumnView
            packages={packages}
            editing={editingApi}
            revealTaskId={revealTaskId}
            onRevealed={clearReveal}
            touchedPackageIds={touchedPackageIds}
            touchedWorkSectionIds={touchedWorkSectionIds}
            touchedTaskIds={touchedTaskIds}
          />
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t bg-muted/20 text-xs text-muted-foreground shrink-0">
          <span>
            <span className="font-semibold text-foreground tabular-nums">
              {packages.length}
            </span>{" "}
            hankintapakettia
          </span>
          <span className="text-muted-foreground/50">·</span>
          <span>
            <span className="font-semibold text-foreground tabular-nums">
              {totalWorkSections}
            </span>{" "}
            työpakettia
          </span>
          <span className="text-muted-foreground/50">·</span>
          <span>
            <span className="font-semibold text-foreground tabular-nums">
              {totalTasks}
            </span>{" "}
            tehtävää
          </span>
          <span className="text-muted-foreground/50">·</span>
          <span>
            <span className="font-semibold text-foreground tabular-nums">
              {totalSubtasks}
            </span>{" "}
            alitehtävää
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
